import Analytics from '@withlocals/withlocals-analytics';
import { Translator } from 'Translator';
import { AddFluxHOCProps, addFlux } from 'components/wrappers/WithFlux';
import { getFilter } from 'data/filters';
import routeToSearchURL from 'modules/homescreen/helpers/routeToSearchURL';
import FiltersProvider from 'providers/FiltersProvider';
import React, { PureComponent } from 'react';
import { analyticsTrack } from 'utils/analytics';

import InputNumberOfPeopleSelector from '../../../../components/InputNumberOfPeople/InputNumberOfPeopleSelector';
import Popover from '../../../../components/Popover';
import { castleFFCities } from '../../../../constants';

const CSS = require('./PeoplePopover.less');

type PeoplePopoverProps = AddFluxHOCProps & {
  onClose: (args: {
    source: 'escape button' | 'outside click' | 'apply button' | 'clear button';
    callback?: () => void;
  }) => void;
};

interface PeoplePopoverState {
  nrOfAdults: number;
  nrOfChildren: number;
}

class PeoplePopover extends PureComponent<
  PeoplePopoverProps,
  PeoplePopoverState
> {
  state = {
    nrOfAdults:
      this.props.flux.stores.FiltersStore.getFilters().activeFilters.find(
        (filter) => filter.uid === 'adults',
      ).value,
    nrOfChildren:
      this.props.flux.stores.FiltersStore.getFilters().activeFilters.find(
        (filter) => filter.uid === 'children',
      ).value,
  };

  isPristine() {
    const { nrOfAdults, nrOfChildren } = this.state;
    return (
      nrOfAdults ===
        this.props.flux.stores.FiltersStore.getFilters().activeFilters.find(
          (filter) => filter.uid === 'adults',
        ).value &&
      nrOfChildren ===
        this.props.flux.stores.FiltersStore.getFilters().activeFilters.find(
          (filter) => filter.uid === 'children',
        ).value
    );
  }

  onApply =
    (source: 'escape button' | 'outside click' | 'apply button') => (e) => {
      e?.stopPropagation();

      const guests = {
        nrOfAdults: this.state.nrOfAdults,
        nrOfChildren: this.state.nrOfChildren,
      };

      if (this.isPristine()) {
        this.props.onClose({ source });
        return;
      }

      const isFamilyFriendlyCity = castleFFCities.includes(
        this.props.flux.stores.FiltersStore.getFilters().city,
      );
      const noChildrenInActiveFilters =
        this.props.flux.stores.FiltersStore.getFilters().activeFilters.find(
          (filter) => filter.uid === 'children',
        ).value === 0;

      const enableFamilyFriendly =
        isFamilyFriendlyCity &&
        noChildrenInActiveFilters &&
        this.state.nrOfChildren > 0;

      const adultsFilter = getFilter('adults');
      const childrenFilter = getFilter('children');
      adultsFilter.value = guests.nrOfAdults;
      childrenFilter.value = guests.nrOfChildren;

      this.props.flux.actions.filters.setGuests(guests);
      this.props.flux.actions.experiencefilter.setGuests(guests);

      Analytics.track('Search page', 'Filter guests', guests);

      if (enableFamilyFriendly) {
        this.props.onClose({
          source,
          callback: () =>
            routeToSearchURL({
              filtersToAdd: [
                adultsFilter,
                childrenFilter,
                getFilter('family-friendly'),
              ],
              replace: true,
            }),
        });
      } else {
        this.props.onClose({
          source,
          callback: () =>
            routeToSearchURL({
              filtersToAdd: [adultsFilter, childrenFilter],
              replace: true,
            }),
        });
      }
    };

  onClear = (e) => {
    e?.stopPropagation();

    this.props.onClose({ source: 'clear button' });
  };

  onGuestsChange = (nrOfAdults, nrOfChildren) => {
    const adultCount = nrOfAdults;
    const childCount = nrOfChildren;
    const guestCount = nrOfAdults + nrOfChildren;
    analyticsTrack(
      {
        type: 'click',
        subject: `people selection`,
        value: { adultCount, childCount, guestCount },
        valueString: `${adultCount} / ${childCount} / ${guestCount}`,
      },
      {
        category: 'Search page',
        action: 'Filter guests',
        label: `${adultCount}, ${childCount}`,
      },
    );

    this.setState({
      nrOfAdults,
      nrOfChildren,
    });
  };

  render() {
    return (
      <FiltersProvider>
        {({ adults, children }) => (
          <Popover
            show
            name="peoplePopover"
            underNavbar
            className={CSS.Popover}
            onClose={this.onApply('escape button')}
            onOutSideClick={this.onApply('outside click')}
          >
            <InputNumberOfPeopleSelector
              onChange={this.onGuestsChange}
              nrOfAdultsInitialValue={adults.value}
              nrOfChildrenInitialValue={children.value}
              maxPeople={20}
            />
            <div className={CSS.ButtonContainer}>
              <button className={CSS.ClearButton} onClick={this.onClear}>
                {Translator.translate('Common.Clear', {}, true, 'Clear')}
              </button>
              <button
                className={CSS.ApplyButton}
                onClick={this.onApply('apply button')}
              >
                {Translator.translate('Common.Apply', {}, true, 'Apply')}
              </button>
            </div>
          </Popover>
        )}
      </FiltersProvider>
    );
  }
}

export default addFlux(PeoplePopover);
